import { Button, Input, Tooltip } from "antd";
import { DeleteOutlined, PlusOutlined, HistoryOutlined } from "@ant-design/icons";

/**
 * AccomplishmentsListField – a dynamic list of multi-line text areas.
 * Each entry maps to one bullet point in the CWA PDF.
 *
 * Integrates with Ant Design Form.Item via value / onChange.
 * value = string[]
 *
 * previousAccomplishments = string[] from the most recent prior submission.
 * If provided and non-empty, the "Load from Previous Submission" button is enabled.
 */
const AccomplishmentsListField = ({ value = [], onChange, previousAccomplishments }) => {
  const items = Array.isArray(value) ? value : [];
  const hasPrevious = Array.isArray(previousAccomplishments) && previousAccomplishments.length > 0;

  const handleChange = (index, text) => {
    const next = [...items];
    next[index] = text;
    onChange?.(next);
  };

  const handleAdd = () => {
    onChange?.([...items, ""]);
  };

  const handleRemove = (index) => {
    const next = items.filter((_, i) => i !== index);
    onChange?.(next);
  };

  const handleLoadPrevious = () => {
    onChange?.(previousAccomplishments);
  };

  const handlePaste = (index, e) => {
    const text = e.clipboardData.getData("text");
    const lines = text
      .split("\n")
      .map((line) =>
        line
          .replace(/^[\s•·◦▪▸\-\*]+/, "")
          .replace(/^\d+[.)]\s*/, "")
          .trim()
      )
      .filter((line) => line.length > 0);

    if (lines.length <= 1) return;

    e.preventDefault();
    const next = [...items];
    next.splice(index, 1, ...lines);
    onChange?.(next);
  };

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Tooltip title={hasPrevious ? undefined : "No previous submission found"}>
          <Button
            size="small"
            icon={<HistoryOutlined />}
            onClick={handleLoadPrevious}
            disabled={!hasPrevious}
          >
            Load from Previous Submission
          </Button>
        </Tooltip>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {items.map((item, index) => (
          <div key={index} style={{ display: "flex", alignItems: "flex-start", width: "100%", gap: 8 }}>
            <span style={{ color: "#888", paddingTop: 6, flexShrink: 0 }}>●</span>
            <Input.TextArea
              value={item}
              placeholder={`Accomplishment ${index + 1}`}
              onChange={(e) => handleChange(index, e.target.value)}
              onPaste={(e) => handlePaste(index, e)}
              autoSize={{ minRows: 2, maxRows: 6 }}
              style={{ flex: 1, minWidth: 0 }}
            />
            <Button
              danger
              type="text"
              icon={<DeleteOutlined />}
              onClick={() => handleRemove(index)}
              disabled={items.length === 1}
              style={{ marginTop: 4, flexShrink: 0 }}
            />
          </div>
        ))}
      </div>
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={handleAdd}
        style={{ marginTop: 8, width: "100%" }}
      >
        Add Accomplishment
      </Button>
    </div>
  );
};

export default AccomplishmentsListField;
